import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  expensesApi,
  Expense,
  CreateExpenseDto,
  UpdateExpenseDto,
  GetExpensesDto,
  ExpensesAnalytics,
  ExpensesByStatusAnalytics,
} from '../api/expenses';
import { safeNumber } from '../utils/formatters';

interface UseExpensesReturn {
  // Data
  expenses: Expense[];
  analytics: ExpensesAnalytics | null;
  statusAnalytics: ExpensesByStatusAnalytics | null;
  loading: boolean;
  error: string | null;
  
  // Operations
  createExpense: (data: CreateExpenseDto) => Promise<Expense | null>;
  updateExpense: (id: string, data: UpdateExpenseDto) => Promise<Expense | null>;
  deleteExpense: (id: string) => Promise<boolean>;
  refreshData: () => Promise<void>;
  
  // Utility functions
  getExpenseById: (id: string) => Expense | undefined;
  getTotalSpent: () => number;
  getExpensesByStatus: (status: string) => Expense[];
  getExpensesByCategory: (category: string) => Expense[];
  getRecentExpenses: (days?: number) => Expense[];
}

export const useExpenses = (initialFilters: GetExpensesDto = {}): UseExpensesReturn => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [analytics, setAnalytics] = useState<ExpensesAnalytics | null>(null);
  const [statusAnalytics, setStatusAnalytics] = useState<ExpensesByStatusAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load expenses data
  const loadExpenses = useCallback(async (filters: GetExpensesDto = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const expensesData = await expensesApi.getExpenses({ ...initialFilters, ...filters });
      setExpenses(expensesData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load expenses';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [initialFilters]);

  // Load analytics data
  const loadAnalytics = useCallback(async () => {
    try {
      const [analyticsData, statusData] = await Promise.all([
        expensesApi.getExpensesAnalytics(),
        expensesApi.getExpensesByStatus(),
      ]);
      
      setAnalytics(analyticsData);
      setStatusAnalytics(statusData);
    } catch (err) {
      console.error('Failed to load expenses analytics:', err);
    }
  }, []);

  // Create expense
  const createExpense = useCallback(async (data: CreateExpenseDto): Promise<Expense | null> => {
    try {
      setLoading(true);
      const newExpense = await expensesApi.createExpense(data);
      
      setExpenses(prev => [newExpense, ...prev]);
      toast.success('Expense created successfully');
      
      // Refresh analytics
      loadAnalytics();
      
      return newExpense;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create expense';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadAnalytics]);

  // Update expense
  const updateExpense = useCallback(async (id: string, data: UpdateExpenseDto): Promise<Expense | null> => {
    try {
      setLoading(true);
      const updatedExpense = await expensesApi.updateExpense(id, data);
      
      setExpenses(prev => 
        prev.map(expense => 
          expense.id === id ? updatedExpense : expense
        )
      );
      
      toast.success('Expense updated successfully');
      
      // Refresh analytics
      loadAnalytics();
      
      return updatedExpense;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update expense';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadAnalytics]);

  // Delete expense
  const deleteExpense = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      await expensesApi.deleteExpense(id);
      
      setExpenses(prev => prev.filter(expense => expense.id !== id));
      toast.success('Expense deleted successfully');
      
      // Refresh analytics
      loadAnalytics();
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete expense';
      setError(errorMessage);
      toast.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadAnalytics]);

  // Refresh data
  const refreshData = useCallback(async () => {
    await Promise.all([
      loadExpenses(),
      loadAnalytics(),
    ]);
  }, [loadExpenses, loadAnalytics]);

  // Utility functions
  const getExpenseById = useCallback((id: string): Expense | undefined => {
    return expenses.find(expense => expense.id === id);
  }, [expenses]);

  const getTotalSpent = useCallback((): number => {
    return expenses.reduce((total, expense) => total + safeNumber(expense.amount), 0);
  }, [expenses]);

  const getExpensesByStatus = useCallback((status: string): Expense[] => {
    return expenses.filter(expense => expense.status === status);
  }, [expenses]);

  const getExpensesByCategory = useCallback((category: string): Expense[] => {
    return expenses.filter(expense => expense.category === category);
  }, [expenses]);

  const getRecentExpenses = useCallback((days = 30): Expense[] => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    return expenses.filter(expense => new Date(expense.transactionDate) >= cutoffDate);
  }, [expenses]);

  // Initialize data loading
  useEffect(() => {
    refreshData();
  }, []); // Empty dependency array means this runs once on mount

  return {
    // Data
    expenses,
    analytics,
    statusAnalytics,
    loading,
    error,
    
    // Operations
    createExpense,
    updateExpense,
    deleteExpense,
    refreshData,
    
    // Utility functions
    getExpenseById,
    getTotalSpent,
    getExpensesByStatus,
    getExpensesByCategory,
    getRecentExpenses,
  };
}; 